/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { MenuList } from '@coursera/cds-core';

import type { IconComponent } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/types';
import { useInternalNavClick } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/useInternalNavClick';

const styles = {
  linkItem: css`
    &&,
    &&:link,
    &&:visited,
    &&:hover,
    &&:focus,
    &&:active {
      text-decoration: none;
      color: var(--cds-color-neutral-primary);
    }
  `,
  content: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
  `,
  icon: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  `,
};

type Props = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href?: string;
  icon: IconComponent;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

/**
 * A menu link item with icon and label.
 * Handles internal navigation and emits onClick callback when activated.
 */
export const MenuItem = ({ href, icon: Icon, children, onClick, ...anchorProps }: Props) => {
  const handleInternalNav = useInternalNavClick(href);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleInternalNav(e);
    onClick?.(e);
  };

  // If there is no href or onClick, don't render the item.
  if (!href && !onClick) return null;

  return (
    <MenuList.ItemLink href={href} onClick={handleClick} css={styles.linkItem} {...anchorProps}>
      <span css={styles.content}>
        <span css={styles.icon} aria-hidden="true">
          <Icon size="medium" />
        </span>

        {children}
      </span>
    </MenuList.ItemLink>
  );
};

export default MenuItem;
